import { intersection, set } from 'lodash'

import { store } from 'store/reducers'
import { Format } from 'services/Dates'

import Api from 'services/Api'

const allValidFlags: MData.UserFlags[] = [
  'hasRestrictions',
  'missingSkills',
  'missingRequirements',
  'missingRegion',
  'vacationOverlapping',
  'eventOverlapping',
  'eventDistance',
  'lockedToProject',
  'preferredOnProject',
  'maxNumberOfHours',
  'maxNumberOfHoursPerDay',
  'maxConsecutiveDays',
  'missingPosition',
  'leaveOverlapping',
  'unavailableAllDay',
  'unavailableOverlap',
  'noCurrentAvailability',
  'pendingAvailability',
  'wrongDepartment',
  'wrongTeam',
  'wrongProject',
  'maxConsecutiveTime',
  'missingAdjacentShiftBefore',
  'missingAdjacentShiftAfter',
  'missingAdjacentShifts',
  'lockedToCurrent',
  'preferredOnCurrent',
  'blockedFromProject',
  'employeeExpiredContract',
  'employeeExpiredPermit',
  'expiredEmployeeType',
  'noContract',
  'noPermit',
  'noEmployeeType',
  'beforeStartDate',
  'afterEndDate',
  'archivedUser',
  'employeeTypeInvalid'
]

type ShiftId = string
export const getValidFlags = (flags: MData.UserWithFlags['flags']['shiftFlags'][ShiftId]): string[] => intersection(allValidFlags, Object.keys(flags))

type ReturnType = {
  success: boolean
  flaggedUsers?: MData.UserWithFlags[]
  numberOfHoursPerRange?: MData.UserWithFlags['numberOfHoursPerRange']
  validFlags?: string[]
  userFlags?: MData.UserWithFlags['flags']['shiftFlags'][ShiftId]
}

type Event = Pick<
  MData.Event,
  '_id' | 'departments' | 'start' | 'startTimezone' | 'end' | 'endTimezone' | 'positionId' | 'projects' | 'team' | 'tags' | 'breaks' | 'requiredAttributes'
> & { employeeId?: string; procedureId?: string }

type GetEventFlags = (event: Event, singleuser: boolean, options?: { filterByAttributesFirst: boolean }) => Promise<ReturnType>

const getEventFlags: GetEventFlags = async (event, singleuser, { filterByAttributesFirst } = { filterByAttributesFirst: false }) => {
  const {
    resources: { filtersById, sortsById, proceduresById }
  } = store.getState() as MStore.RootState

  const procedureFilter = event.procedureId ? proceduresById[event.procedureId]?.userFilter : ''
  const procedureSort = event.procedureId ? proceduresById[event.procedureId]?.userSort : ''

  const filterConfig = filtersById[procedureFilter] || {}
  const sortConfig = sortsById[procedureSort] || {}

  let eventProjects = event.projects
  if (!Array.isArray(event.projects)) eventProjects = event.projects ? [event.projects] : []

  if (!filterConfig.options) filterConfig.options = {}

  const fields = {
    firstName: 1,
    lastName: 1,
    _id: 1,
    hiringData: 1,
    employeeNumber: 1,
    specificRestrictions: 1,
    projectRestrictions: 1,
    regionalTags: 1,
    priorityNumber: 1,
    occupations: 1
  }

  const flagConfig = {
    dates: [],
    events: [
      {
        ...event,
        start: Format(event.start),
        end: Format(event.end),
        projects: eventProjects
      }
    ],
    departments: event.departments,
    flags: filterConfig.flags,
    options: { ...filterConfig.options, filterByAttributesFirst },
    sorts: (sortConfig.usedSorts || []).map(sort => {
      if (sort.id === 'projectExperienceSort') set(sort, 'options.projects', eventProjects)
      return sort
    })
  }

  const { users, success }: { users: MData.UserWithFlags[]; success: boolean } = await Api.post('users/flags', {
    _id: event.employeeId,
    fields,
    flagConfig
  }) // we need to type the users object

  if (!success || (singleuser && !users[0])) return { success: false }

  // When we only want the first user to be returned
  if (!singleuser) {
    return { flaggedUsers: users, success }
  }

  const userFlags = users[0].flags.shiftFlags[event._id]
  return { userFlags, numberOfHoursPerRange: users[0].numberOfHoursPerRange, validFlags: getValidFlags(userFlags), success }
}

export default getEventFlags
